import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import styles from "./DoctorPortal.module.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import {
  faPlusCircle, faBoxOpen, faStore, faTimes, faUpload, faImage, faTrash, faCheckCircle,
  faClipboardList, faFilePdf, faSort, faCalendarAlt, faReceipt
} from "@fortawesome/free-solid-svg-icons";

const CATEGORIES = ["Orthopedic", "Diagnostics", "Wellness", "Nutrition", "Rehabilitation", "General"];

const ShopManager = () => {
  const [activeTab, setActiveTab] = useState("products"); // "products" | "orders"

  const [products, setProducts] = useState([]);
  const [loading,  setLoading]  = useState(true);
  const [saving,   setSaving]   = useState(false);
  const [form, setForm] = useState({
    title: "", description: "", price: "", stockQty: "", category: "General",
    coverImage: "", galleryImages: [], features: ["", "", "", ""],
  });
  const [featInput, setFeatInput] = useState("");
  const [success, setSuccess]     = useState(false);
  const fileInputRef              = useRef(null);

  // Orders state
  const [orders, setOrders]               = useState([]);
  const [ordersLoading, setOrdersLoading] = useState(false);
  const [sortOrder, setSortOrder]         = useState("newest"); // "newest" | "oldest"

  const token = localStorage.getItem("token");

  useEffect(() => {
    const loadProducts = async () => {
      try {
        const res = await axios.get("/api/shop/products");
        setProducts(res.data.products || []);
      } catch (err) {
        console.error("Error loading products:", err);
      } finally {
        setLoading(false);
      }
    };
    loadProducts();
  }, []);

  const loadOrders = async () => {
    setOrdersLoading(true);
    try {
      const res = await axios.get("/api/shop/orders", {
        headers: { Authorization: `Bearer ${token}` }
      });
      setOrders(res.data.orders || []);
    } catch (err) {
      console.error("Error loading orders:", err);
    } finally {
      setOrdersLoading(false);
    }
  };

  useEffect(() => {
    if (activeTab === "orders" && orders.length === 0) {
      loadOrders();
    }
  }, [activeTab]);

  const handleChange = (field, val) => setForm((p) => ({ ...p, [field]: val }));

  const handleImageUpload = (e) => {
    const file = e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onloadend = () => handleChange("coverImage", reader.result);
    reader.readAsDataURL(file);
  };

  const addFeature = () => {
    if (featInput.trim()) {
      setForm((p) => ({ ...p, features: [...p.features.filter(Boolean), featInput.trim()] }));
      setFeatInput("");
    }
  };
  const removeFeature = (i) => setForm((p) => ({ ...p, features: p.features.filter((_, idx) => idx !== i) }));

  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.title || !form.description || !form.price) return;
    setSaving(true);
    try {
      const payload = {
        ...form,
        features: form.features.filter(Boolean),
        price: parseFloat(form.price),
        stockQty: parseInt(form.stockQty) || 0,
      };
      const res = await axios.post("/api/shop/products", payload, {
        headers: { Authorization: `Bearer ${token}` },
      });
      setProducts((p) => [res.data.product, ...p]);
      setForm({ title: "", description: "", price: "", stockQty: "", category: "General", coverImage: "", galleryImages: [], features: [] });
      setSuccess(true);
      setTimeout(() => setSuccess(false), 3000);
    } catch (err) {
      alert("Failed to create product: " + (err.response?.data?.message || err.message));
    } finally {
      setSaving(false);
    }
  };

  const deleteProduct = async (id) => {
    if (!window.confirm("Delete this product from the shop?")) return;
    try {
      await axios.delete(`/api/shop/products/${id}`, { headers: { Authorization: `Bearer ${token}` } });
      setProducts((p) => p.filter((pr) => pr._id !== id));
    } catch {
      alert("Failed to delete product.");
    }
  };

  // Packing Slip / PDF Receipt download generator
  const downloadOrderReceipt = (order) => {
    const printWindow = window.open("", "_blank", "width=850,height=950");
    const patientName = order.patientId?.name || `${order.patientId?.firstName || ""} ${order.patientId?.lastName || ""}`.trim() || order.shippingAddress?.fullName || "Verified Patient";
    const patientEmail = order.patientId?.email || "";
    const patientPhone = order.patientId?.mobileNumber || order.patientId?.contactNumber || order.patientId?.phone || order.shippingAddress?.phone || "No Mobile Provided";
    const dateStr = new Date(order.createdAt || Date.now()).toLocaleDateString("en-IN", {
      year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit"
    });

    const itemsHtml = (order.items || []).map((i, idx) => `
      <tr>
        <td style="padding: 12px 10px; border-bottom: 1px solid #e2e8f0;">${idx + 1}. ${i.title || "Medical Wellness Product"}</td>
        <td style="padding: 12px 10px; border-bottom: 1px solid #e2e8f0; text-align: center; font-weight: bold;">${i.qty}</td>
        <td style="padding: 12px 10px; border-bottom: 1px solid #e2e8f0; text-align: right;">₹${((i.price || 0) * 83).toFixed(0)}</td>
        <td style="padding: 12px 10px; border-bottom: 1px solid #e2e8f0; text-align: right; font-weight: bold;">₹${(((i.price || 0) * 83) * i.qty).toFixed(0)}</td>
      </tr>
    `).join("");

    const subINR   = (order.totalAmount || 0) * 83;
    const shipINR  = subINR >= 500 ? 0 : 49;
    const totalINR = subINR >= 500 ? subINR : subINR + 49;

    printWindow.document.write(`
      <!DOCTYPE html>
      <html>
      <head>
        <title>Dispatch_Receipt_Order_${String(order._id).slice(-8).toUpperCase()}</title>
        <style>
          @page { size: A4; margin: 20mm; }
          body { font-family: 'Helvetica Neue', Helvetica, Arial, sans-serif; color: #0f172a; margin: 0; padding: 20px; line-height: 1.5; }
          .header { display: flex; justify-content: space-between; align-items: flex-start; border-bottom: 3px solid #2d6a4f; padding-bottom: 20px; margin-bottom: 25px; }
          .brand { font-size: 28px; font-weight: 900; color: #2d6a4f; letter-spacing: -0.5px; }
          .sub-brand { font-size: 13px; color: #64748b; font-weight: 600; text-transform: uppercase; letter-spacing: 1px; }
          .doc-title { font-size: 22px; font-weight: 900; text-align: right; color: #0f172a; }
          
          /* Shipping Label Box */
          .shipping-label { border: 2.5px solid #0f172a; border-radius: 12px; padding: 20px; margin-bottom: 30px; background: #f8fafc; position: relative; }
          .label-badge { position: absolute; top: -12px; left: 20px; background: #0f172a; color: #ffffff; font-size: 11px; font-weight: 800; padding: 4px 12px; border-radius: 9999px; text-transform: uppercase; letter-spacing: 1px; }
          .ship-to-name { font-size: 20px; font-weight: 900; color: #0f172a; margin-top: 6px; margin-bottom: 4px; }
          .ship-to-addr { font-size: 16px; color: #334155; line-height: 1.4; }
          .ship-to-pin { font-size: 18px; font-weight: 900; color: #0f172a; }
          
          .barcode-box { text-align: center; margin: 25px 0; padding: 15px; border: 1.5px dashed #cbd5e1; border-radius: 8px; background: #ffffff; }
          .barcode-text { font-family: 'Courier New', monospace; font-size: 22px; font-weight: bold; letter-spacing: 6px; color: #1e293b; }
          
          table { width: 100%; border-collapse: collapse; margin-top: 10px; }
          th { background: #f1f5f9; padding: 12px 10px; text-align: left; font-size: 12px; font-weight: 800; text-transform: uppercase; color: #475569; border-bottom: 2px solid #cbd5e1; }
          
          .totals-wrapper { margin-top: 25px; display: flex; justify-content: flex-end; }
          .totals-box { width: 280px; }
          .row { display: flex; justify-content: space-between; padding: 8px 0; font-size: 14px; color: #475569; }
          .row.final { border-top: 2px solid #0f172a; padding-top: 12px; margin-top: 6px; font-size: 18px; font-weight: 900; color: #2d6a4f; }
          
          .footer { margin-top: 50px; padding-top: 20px; border-top: 1px solid #e2e8f0; text-align: center; font-size: 12px; color: #64748b; }
        </style>
      </head>
      <body>
        <div class="header">
          <div>
            <div class="brand">ReformMe Healthcare</div>
            <div class="sub-brand">Hospital-Grade Wellness Dispatch</div>
          </div>
          <div>
            <div class="doc-title">PACKING SLIP & TAX INVOICE</div>
            <div style="font-size: 14px; font-weight: 700; color: #475569; text-align: right; margin-top: 4px;">Order #${String(order._id).slice(-8).toUpperCase()}</div>
            <div style="font-size: 13px; color: #64748b; text-align: right;">Date: ${dateStr}</div>
          </div>
        </div>

        <!-- Shipping Label -->
        <div class="shipping-label">
          <div class="label-badge">DELIVERY DISPATCH LABEL</div>
          <div class="ship-to-name">To: ${patientName}</div>
          <div class="ship-to-addr">${order.shippingAddress?.street || "No street provided"}</div>
          <div class="ship-to-addr">${order.shippingAddress?.city || ""}, ${order.shippingAddress?.state || ""} — <span class="ship-to-pin">${order.shippingAddress?.zip || ""}</span></div>
          <div style="font-size: 14px; color: #0f172a; margin-top: 8px; font-weight: 800;">📱 Mobile No: ${patientPhone} <span style="color: #64748b; font-weight: 600;">| Email: ${patientEmail || "N/A"}</span></div>
        </div>

        <div class="barcode-box">
          <div style="font-size: 10px; color: #94a3b8; text-transform: uppercase; font-weight: 800; margin-bottom: 4px;">DISPATCH TRACKING BARCODE</div>
          <div class="barcode-text">*TRX-${order.transactionId || String(order._id).slice(-10)}*</div>
        </div>

        <h3 style="font-size: 16px; font-weight: 800; margin-bottom: 10px; color: #0f172a;">Ordered Products Summary</h3>
        <table>
          <thead>
            <tr>
              <th>Item Description</th>
              <th style="text-align: center;">Qty</th>
              <th style="text-align: right;">Unit Price</th>
              <th style="text-align: right;">Total Amount</th>
            </tr>
          </thead>
          <tbody>
            ${itemsHtml}
          </tbody>
        </table>

        <div class="totals-wrapper">
          <div class="totals-box">
            <div class="row"><span>Subtotal:</span><span style="font-weight: 700; color: #0f172a;">₹${subINR.toFixed(0)}</span></div>
            <div class="row"><span>Shipping & Handling:</span><span style="font-weight: 700; color: ${shipINR === 0 ? '#2d6a4f' : '#0f172a'}">${shipINR === 0 ? "FREE" : `₹${shipINR}`}</span></div>
            <div class="row final"><span>Total Paid:</span><span>₹${totalINR.toFixed(0)}</span></div>
          </div>
        </div>

        <div class="footer">
          <p style="margin: 0 0 6px; font-weight: 700; color: #334155;">Thank you for trusting ReformMe Healthcare for your medical wellness needs.</p>
          <p style="margin: 0;">This is an electronically generated packing label and receipt valid for all warranty and medical insurance claims.</p>
        </div>

        <script>
          window.onload = () => {
            setTimeout(() => {
              window.print();
            }, 300);
          };
        </script>
      </body>
      </html>
    `);
    printWindow.document.close();
  };

  const sortedOrders = [...orders].sort((a, b) => {
    const dateA = new Date(a.createdAt || 0).getTime();
    const dateB = new Date(b.createdAt || 0).getTime();
    return sortOrder === "newest" ? dateB - dateA : dateA - dateB;
  });

  return (
    <div>
      <div className={styles.viewHeader} style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "1rem" }}>
        <div>
          <h1 className={styles.viewTitle}>Wellness Shop & Dispatch</h1>
          <p className={styles.viewSubtitle}>Manage shop listings and download customer dispatch receipts</p>
        </div>

        {/* Top Option Toggle Tabs */}
        <div style={{ display: "flex", gap: "10px", background: "#f1f5f9", padding: "6px", borderRadius: "9999px", border: "1px solid #e2e8f0" }}>
          <button
            type="button"
            onClick={() => setActiveTab("products")}
            style={{
              padding: "0.6rem 1.4rem", borderRadius: "9999px", border: "none", fontWeight: 800, fontSize: "0.88rem", cursor: "pointer", transition: "all 0.2s", display: "flex", alignItems: "center", gap: "8px",
              background: activeTab === "products" ? "#2d6a4f" : "transparent",
              color: activeTab === "products" ? "#ffffff" : "#64748b",
              boxShadow: activeTab === "products" ? "0 4px 12px rgba(45,106,79,0.25)" : "none"
            }}
          >
            <FontAwesomeIcon icon={faStore} /> 📦 Products Management
          </button>
          <button
            type="button"
            onClick={() => setActiveTab("orders")}
            style={{
              padding: "0.6rem 1.4rem", borderRadius: "9999px", border: "none", fontWeight: 800, fontSize: "0.88rem", cursor: "pointer", transition: "all 0.2s", display: "flex", alignItems: "center", gap: "8px",
              background: activeTab === "orders" ? "#2d6a4f" : "transparent",
              color: activeTab === "orders" ? "#ffffff" : "#64748b",
              boxShadow: activeTab === "orders" ? "0 4px 12px rgba(45,106,79,0.25)" : "none"
            }}
          >
            <FontAwesomeIcon icon={faClipboardList} /> 📋 Orders & Receipts ({orders.length})
          </button>
        </div>
      </div>

      {/* ─── TAB 1: PRODUCTS MANAGEMENT ──────────────────────────────────────── */}
      {activeTab === "products" && (
        <>
          {/* Add Product Form */}
          <div className={styles.glassCard} style={{ marginBottom: "2rem" }}>
            <h3 style={{ fontSize: "1.1rem", fontWeight: 800, color: "#0f172a", margin: "0 0 1.5rem", display: "flex", alignItems: "center", gap: "8px" }}>
              <FontAwesomeIcon icon={faPlusCircle} style={{ color: "#2d6a4f" }} />
              Add New Product
            </h3>

            {success && (
              <div style={{ display: "flex", alignItems: "center", gap: "8px", padding: "0.75rem 1.25rem", background: "#f0fdf4", border: "1.5px solid #86efac", borderRadius: "12px", color: "#166534", fontWeight: 700, marginBottom: "1.25rem" }}>
                <FontAwesomeIcon icon={faCheckCircle} /> Product listed successfully!
              </div>
            )}

            <form onSubmit={handleSubmit}>
              <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "1.25rem" }}>
                <div style={{ gridColumn: "1 / -1" }}>
                  <label style={{ display: "block", fontSize: "0.82rem", fontWeight: 750, color: "#475569", marginBottom: "6px" }}>Product Title *</label>
                  <input
                    className={styles.inputField}
                    placeholder="e.g. ErgoSpine Premium Lumbar Cushion"
                    value={form.title}
                    onChange={(e) => handleChange("title", e.target.value)}
                    required
                  />
                </div>

                <div>
                  <label style={{ display: "block", fontSize: "0.82rem", fontWeight: 750, color: "#475569", marginBottom: "6px" }}>Price (USD) *</label>
                  <input
                    className={styles.inputField}
                    type="number"
                    placeholder="45.00"
                    value={form.price}
                    onChange={(e) => handleChange("price", e.target.value)}
                    min="0" step="0.01" required
                  />
                </div>

                <div>
                  <label style={{ display: "block", fontSize: "0.82rem", fontWeight: 750, color: "#475569", marginBottom: "6px" }}>Stock Quantity</label>
                  <input
                    className={styles.inputField}
                    type="number"
                    placeholder="100"
                    value={form.stockQty}
                    onChange={(e) => handleChange("stockQty", e.target.value)}
                    min="0"
                  />
                </div>

                <div>
                  <label style={{ display: "block", fontSize: "0.82rem", fontWeight: 750, color: "#475569", marginBottom: "6px" }}>Category</label>
                  <select
                    className={styles.inputField}
                    value={form.category}
                    onChange={(e) => handleChange("category", e.target.value)}
                  >
                    {CATEGORIES.map((c) => <option key={c}>{c}</option>)}
                  </select>
                </div>

                {/* Cover Image */}
                <div style={{ gridColumn: "1 / -1" }}>
                  <label style={{ display: "block", fontSize: "0.82rem", fontWeight: 750, color: "#475569", marginBottom: "8px" }}>Cover Image</label>
                  <div style={{ display: "flex", gap: "12px", alignItems: "center", flexWrap: "wrap" }}>
                    <input type="file" accept="image/*" ref={fileInputRef} style={{ display: "none" }} onChange={handleImageUpload} />
                    <button
                      type="button"
                      onClick={() => fileInputRef.current?.click()}
                      style={{ padding: "0.55rem 1.25rem", background: "#2d6a4f", color: "#fff", border: "none", borderRadius: "9999px", fontWeight: 700, cursor: "pointer", fontSize: "0.85rem", display: "flex", alignItems: "center", gap: "6px", fontFamily: "inherit" }}
                    >
                      <FontAwesomeIcon icon={faUpload} /> Upload File
                    </button>
                    <span style={{ color: "#94a3b8", fontWeight: 700, fontSize: "0.85rem" }}>OR</span>
                    <div style={{ flex: 1, minWidth: "220px", position: "relative" }}>
                      <FontAwesomeIcon icon={faImage} style={{ position: "absolute", left: "14px", top: "50%", transform: "translateY(-50%)", color: "#94a3b8" }} />
                      <input
                        className={styles.inputField}
                        placeholder="Paste image URL..."
                        value={form.coverImage}
                        onChange={(e) => handleChange("coverImage", e.target.value)}
                        style={{ paddingLeft: "2.5rem" }}
                      />
                    </div>
                  </div>
                  {form.coverImage && (
                    <div style={{ marginTop: "10px", position: "relative", display: "inline-block" }}>
                      <img src={form.coverImage} alt="Preview" style={{ height: "130px", borderRadius: "12px", objectFit: "cover", border: "2px solid #e2e8f0" }} />
                      <button type="button" onClick={() => handleChange("coverImage", "")}
                        style={{ position: "absolute", top: "6px", right: "6px", background: "rgba(239,68,68,0.9)", color: "#fff", border: "none", borderRadius: "50%", width: "26px", height: "26px", cursor: "pointer", display: "flex", alignItems: "center", justifyContent: "center", fontSize: "0.75rem" }}>
                        <FontAwesomeIcon icon={faTimes} />
                      </button>
                    </div>
                  )}
                </div>

                <div style={{ gridColumn: "1 / -1" }}>
                  <label style={{ display: "block", fontSize: "0.82rem", fontWeight: 750, color: "#475569", marginBottom: "6px" }}>Description *</label>
                  <textarea
                    className={styles.inputField}
                    rows={4}
                    placeholder="Detailed clinical or wellness description..."
                    value={form.description}
                    onChange={(e) => handleChange("description", e.target.value)}
                    style={{ resize: "vertical", fontFamily: "inherit", lineHeight: "1.6" }}
                    required
                  />
                </div>

                {/* Features */}
                <div style={{ gridColumn: "1 / -1" }}>
                  <label style={{ display: "block", fontSize: "0.82rem", fontWeight: 750, color: "#475569", marginBottom: "8px" }}>Key Features</label>
                  <div style={{ display: "flex", gap: "8px", marginBottom: "10px" }}>
                    <input
                      className={styles.inputField}
                      placeholder="Add a feature and press Enter"
                      value={featInput}
                      onChange={(e) => setFeatInput(e.target.value)}
                      onKeyDown={(e) => { if (e.key === "Enter") { e.preventDefault(); addFeature(); } }}
                      style={{ flex: 1 }}
                    />
                    <button type="button" onClick={addFeature}
                      style={{ padding: "0.55rem 1.1rem", background: "#f0fdf4", color: "#2d6a4f", border: "1.5px solid #86efac", borderRadius: "12px", fontWeight: 700, cursor: "pointer", fontFamily: "inherit" }}>
                      + Add
                    </button>
                  </div>
                  {form.features.filter(Boolean).map((f, i) => (
                    <span key={i} style={{ display: "inline-flex", alignItems: "center", gap: "6px", background: "#f0fdf4", border: "1px solid #86efac", color: "#166534", padding: "4px 12px", borderRadius: "9999px", fontSize: "0.82rem", fontWeight: 700, margin: "3px" }}>
                      {f}
                      <button type="button" onClick={() => removeFeature(i)} style={{ background: "none", border: "none", color: "#ef4444", cursor: "pointer", padding: 0, fontSize: "0.85rem" }}>×</button>
                    </span>
                  ))}
                </div>
              </div>

              <div style={{ display: "flex", justifyContent: "flex-end", marginTop: "1.5rem" }}>
                <button
                  type="submit"
                  disabled={saving}
                  className={styles.primaryBtn}
                >
                  <FontAwesomeIcon icon={faStore} />
                  <span>{saving ? "Listing..." : "List in Shop"}</span>
                </button>
              </div>
            </form>
          </div>

          {/* Existing Products */}
          <div className={styles.glassCard}>
            <h3 style={{ fontSize: "1.1rem", fontWeight: 800, color: "#0f172a", margin: "0 0 1.25rem", display: "flex", alignItems: "center", gap: "8px" }}>
              <FontAwesomeIcon icon={faBoxOpen} style={{ color: "#2d6a4f" }} />
              Listed Products ({products.length})
            </h3>
            {loading ? (
              <p style={{ color: "#94a3b8", textAlign: "center", padding: "2rem" }}>Loading products...</p>
            ) : products.length === 0 ? (
              <p style={{ color: "#94a3b8", textAlign: "center", padding: "2rem" }}>No products listed yet.</p>
            ) : (
              <div style={{ display: "grid", gridTemplateColumns: "repeat(auto-fill, minmax(260px, 1fr))", gap: "1.25rem" }}>
                {products.map((p) => (
                  <div key={p._id} style={{ border: "1.5px solid #f1f5f9", borderRadius: "16px", overflow: "hidden", background: "#fafafa" }}>
                    <div style={{ height: "150px", background: "#e2e8f0", overflow: "hidden" }}>
                      <img
                        src={p.coverImage || "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=400&q=60"}
                        alt={p.title}
                        style={{ width: "100%", height: "100%", objectFit: "cover" }}
                        onError={(e) => { e.target.src = "https://images.unsplash.com/photo-1576091160399-112ba8d25d1d?w=400&q=60"; }}
                      />
                    </div>
                    <div style={{ padding: "1rem" }}>
                      <div style={{ fontSize: "0.72rem", fontWeight: 800, color: "#2d6a4f", textTransform: "uppercase", marginBottom: "4px" }}>{p.category}</div>
                      <h4 style={{ margin: "0 0 8px", fontSize: "0.95rem", fontWeight: 800, color: "#0f172a", lineHeight: 1.3 }}>{p.title}</h4>
                      <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center" }}>
                        <span style={{ fontWeight: 900, fontSize: "1.1rem", color: "#0f172a" }}>${p.price.toFixed(2)}</span>
                        <span style={{ fontSize: "0.78rem", color: p.stockQty > 0 ? "#166534" : "#991b1b", fontWeight: 700, background: p.stockQty > 0 ? "#f0fdf4" : "#fef2f2", padding: "3px 10px", borderRadius: "9999px" }}>
                          {p.stockQty > 0 ? `Stock: ${p.stockQty}` : "Out of Stock"}
                        </span>
                      </div>
                      <button onClick={() => deleteProduct(p._id)}
                        style={{ marginTop: "10px", width: "100%", padding: "0.5rem", background: "#fef2f2", color: "#ef4444", border: "1.5px solid #fecaca", borderRadius: "10px", fontWeight: 700, cursor: "pointer", fontFamily: "inherit", fontSize: "0.82rem", display: "flex", alignItems: "center", justifyContent: "center", gap: "6px" }}>
                        <FontAwesomeIcon icon={faTrash} /> Remove from Shop
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </>
      )}

      {/* ─── TAB 2: ORDERS & RECEIPTS ────────────────────────────────────────── */}
      {activeTab === "orders" && (
        <div className={styles.glassCard}>
          <div style={{ display: "flex", justifyContent: "space-between", alignItems: "center", flexWrap: "wrap", gap: "1rem", marginBottom: "1.5rem", paddingBottom: "1rem", borderBottom: "1px solid #f1f5f9" }}>
            <h3 style={{ fontSize: "1.15rem", fontWeight: 800, color: "#0f172a", margin: 0, display: "flex", alignItems: "center", gap: "8px" }}>
              <FontAwesomeIcon icon={faReceipt} style={{ color: "#2d6a4f" }} />
              Customer Orders ({orders.length})
            </h3>

            {/* Sort Controls */}
            <div style={{ display: "flex", alignItems: "center", gap: "10px" }}>
              <span style={{ fontSize: "0.85rem", fontWeight: 700, color: "#64748b" }}>
                <FontAwesomeIcon icon={faSort} /> Sort By:
              </span>
              <select
                value={sortOrder}
                onChange={(e) => setSortOrder(e.target.value)}
                style={{ padding: "0.5rem 1rem", borderRadius: "9999px", border: "1.5px solid #cbd5e1", background: "#ffffff", fontWeight: 700, color: "#0f172a", fontSize: "0.85rem", outline: "none", cursor: "pointer" }}
              >
                <option value="newest">Newest Orders First</option>
                <option value="oldest">Oldest Orders First</option>
              </select>
              <button
                type="button"
                onClick={loadOrders}
                style={{ padding: "0.5rem 1rem", background: "#f1f5f9", color: "#334155", border: "1px solid #cbd5e1", borderRadius: "9999px", fontWeight: 700, fontSize: "0.82rem", cursor: "pointer" }}
              >
                ↻ Refresh
              </button>
            </div>
          </div>

          {ordersLoading ? (
            <p style={{ color: "#64748b", textAlign: "center", padding: "3rem 0", fontWeight: 700 }}>⏳ Loading patient orders...</p>
          ) : sortedOrders.length === 0 ? (
            <div style={{ textAlign: "center", padding: "4rem 1rem", color: "#94a3b8" }}>
              <div style={{ fontSize: "3rem", marginBottom: "1rem" }}>📭</div>
              <h4 style={{ fontWeight: 800, color: "#475569", margin: "0 0 6px" }}>No Orders Found</h4>
              <p style={{ fontSize: "0.9rem" }}>When patients complete checkout in the shop, their dispatch orders will appear here.</p>
            </div>
          ) : (
            <div style={{ display: "flex", flexDirection: "column", gap: "1.25rem" }}>
              {sortedOrders.map((order) => {
                const pName = order.patientId?.name || `${order.patientId?.firstName || ""} ${order.patientId?.lastName || ""}`.trim() || order.shippingAddress?.fullName || "Verified Patient";
                const pPhone = order.patientId?.mobileNumber || order.patientId?.contactNumber || order.patientId?.phone || order.shippingAddress?.phone || "No Mobile Provided";
                const dateStr = new Date(order.createdAt || Date.now()).toLocaleDateString("en-IN", {
                  year: "numeric", month: "short", day: "numeric", hour: "2-digit", minute: "2-digit"
                });
                const subINR = (order.totalAmount || 0) * 83;
                const totalINR = subINR >= 500 ? subINR : subINR + 49;

                return (
                  <div key={order._id} style={{ border: "1.5px solid #e2e8f0", borderRadius: "16px", padding: "1.25rem", background: "#ffffff", boxShadow: "0 4px 12px rgba(15,23,42,0.02)" }}>
                    <div style={{ display: "flex", justifyContent: "space-between", alignItems: "flex-start", flexWrap: "wrap", gap: "1rem", marginBottom: "1rem", paddingBottom: "0.75rem", borderBottom: "1px dashed #e2e8f0" }}>
                      <div>
                        <div style={{ display: "flex", alignItems: "center", gap: "8px", marginBottom: "4px" }}>
                          <span style={{ fontWeight: 900, fontSize: "1.05rem", color: "#0f172a" }}>Order #{String(order._id).slice(-8).toUpperCase()}</span>
                          <span style={{
                            background: order.paymentMethod === "COD" ? "#fef3c7" : "#dcfce7",
                            color: order.paymentMethod === "COD" ? "#d97706" : "#166534",
                            padding: "2px 10px", borderRadius: "9999px", fontSize: "0.75rem", fontWeight: 800
                          }}>
                            {order.paymentMethod === "COD" ? "💵 COD" : "✅ PAID"}
                          </span>
                        </div>
                        <div style={{ fontSize: "0.82rem", color: "#64748b", display: "flex", alignItems: "center", gap: "6px" }}>
                          <FontAwesomeIcon icon={faCalendarAlt} /> Placed on: {dateStr}
                        </div>
                      </div>

                      <div style={{ textAlign: "right" }}>
                        <div style={{ fontSize: "1.3rem", fontWeight: 900, color: "#2d6a4f" }}>₹{totalINR.toFixed(0)}</div>
                        <div style={{ fontSize: "0.75rem", color: "#94a3b8", fontWeight: 600 }}>(${order.totalAmount?.toFixed(2)} USD)</div>
                      </div>
                    </div>

                    <div style={{ display: "flex", flexWrap: "wrap", justifyContent: "space-between", gap: "1.25rem", alignItems: "center" }}>
                      <div style={{ flex: "1 1 300px", minWidth: "250px" }}>
                        <h4 style={{ margin: "0 0 6px", fontSize: "0.88rem", fontWeight: 800, color: "#334155", textTransform: "uppercase", letterSpacing: "0.5px" }}>📦 Delivery Dispatch Info:</h4>
                        <p style={{ margin: "0 0 4px", fontSize: "0.95rem", fontWeight: 800, color: "#0f172a" }}>{pName} <span style={{ fontWeight: 700, color: "#2d6a4f", fontSize: "0.88rem", marginLeft: "6px" }}>📱 {pPhone}</span></p>
                        <p style={{ margin: 0, fontSize: "0.88rem", color: "#475569" }}>{order.shippingAddress?.street}, {order.shippingAddress?.city}, {order.shippingAddress?.state} — <strong style={{ color: "#0f172a" }}>{order.shippingAddress?.zip}</strong></p>
                        <div style={{ marginTop: "10px", display: "flex", flexWrap: "wrap", gap: "6px" }}>
                          {(order.items || []).map((item, idx) => (
                            <span key={idx} style={{ background: "#f8fafc", border: "1px solid #cbd5e1", padding: "4px 10px", borderRadius: "8px", fontSize: "0.8rem", fontWeight: 700, color: "#334155" }}>
                              {item.qty}× {item.title}
                            </span>
                          ))}
                        </div>
                      </div>

                      <div style={{ flexShrink: 0 }}>
                        <button
                          type="button"
                          onClick={() => downloadOrderReceipt(order)}
                          style={{
                            padding: "0.75rem 1.5rem", background: "linear-gradient(135deg, #0f172a, #1e293b)", color: "#ffffff", border: "none", borderRadius: "9999px", fontWeight: 800, fontSize: "0.88rem", cursor: "pointer", display: "flex", alignItems: "center", gap: "8px", boxShadow: "0 4px 12px rgba(15,23,42,0.2)", transition: "all 0.2s"
                          }}
                          onMouseOver={(e) => e.currentTarget.style.transform = "translateY(-2px)"}
                          onMouseOut={(e) => e.currentTarget.style.transform = "translateY(0)"}
                        >
                          <FontAwesomeIcon icon={faFilePdf} style={{ color: "#ef4444", fontSize: "1.1rem" }} />
                          <span>Download Receipt (PDF)</span>
                        </button>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default ShopManager;
